import { cache as reactCache } from 'react';
import { API_SERVER_BASE_URL, getApiBaseUrl } from '@/lib/config';
import {
  ADMIN_GAM_UNIT_DEFAULTS,
  ADMIN_GAM_UNIT_KEYS,
  AD_SLOT_DEFINITIONS,
  GAM_NETWORK_PATH_DEFAULT,
  getAdSlotDefinition,
} from '@/lib/ad-slots';

export type PublicSiteConfig = {
  ga_measurement_id: string;
  facebook_app_id: string;
  ads_enabled: string;
  ads_demo: string;
  ads_provider: string;
  adsense_client: string;
  gam_network_path: string;
  adsense_slot_article_top: string;
  adsense_slot_article_mid: string;
  adsense_slot_article_bottom: string;
  adsense_slot_sidebar: string;
  adsense_slot_home_top: string;
  adsense_slot_home_middle: string;
  adsense_slot_home_bottom: string;
  adsense_slot_home_mid: string;
  gam_ad_unit_path: string;
  gam_unit_masthead: string;
  gam_unit_masthead_top: string;
  gam_unit_web_300x250: string;
  gam_unit_web_300x600: string;
  gam_unit_skyscraper_left: string;
  gam_unit_skyscraper_right: string;
  gam_unit_pageskin_left: string;
  gam_unit_pageskin_right: string;
  gam_unit_interstitial: string;
  gam_unit_pixel: string;
  ads_slot_article_top_enabled: string;
  ads_slot_article_mid_enabled: string;
  ads_slot_article_bottom_enabled: string;
  ads_slot_sidebar_enabled: string;
  ads_slot_home_top_enabled: string;
  ads_slot_home_middle_enabled: string;
  ads_slot_home_bottom_enabled: string;
  ads_slot_home_mid_enabled: string;
  ads_slot_category_top_enabled: string;
  ads_slot_sidebar_tall_enabled: string;
  ads_slot_skyscraper_left_enabled: string;
  ads_slot_skyscraper_right_enabled: string;
  ads_slot_pageskin_left_enabled: string;
  ads_slot_pageskin_right_enabled: string;
  ads_slot_interstitial_enabled: string;
  ads_slot_pixel_enabled: string;
  ads_global_refresh_sec: string;
  youtube_shorts_enabled: string;
  article_video_autoplay: string;
  article_video_initial_volume: string;
  google_preferred_source_enabled: string;
  user_submissions_enabled: string;
  comments_enabled: string;
  guest_comments_enabled: string;
};

const EMPTY = {
  ga_measurement_id: '',
  facebook_app_id: '1291989031661965',
  ads_enabled: '1',
  ads_demo: '0',
  ads_provider: 'gam',
  adsense_client: '',
  gam_network_path: GAM_NETWORK_PATH_DEFAULT,
  gam_ad_unit_path: GAM_NETWORK_PATH_DEFAULT,
  adsense_slot_article_top: '',
  adsense_slot_article_mid: '',
  adsense_slot_article_bottom: '',
  adsense_slot_sidebar: '',
  adsense_slot_home_top: '',
  adsense_slot_home_middle: '',
  adsense_slot_home_bottom: '',
  adsense_slot_home_mid: '',
  gam_unit_masthead: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_masthead,
  gam_unit_masthead_top: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_masthead_top,
  gam_unit_web_300x250: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_web_300x250,
  gam_unit_web_300x600: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_web_300x600,
  gam_unit_skyscraper_left: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_skyscraper_left,
  gam_unit_skyscraper_right: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_skyscraper_right,
  gam_unit_pageskin_left: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_pageskin_left,
  gam_unit_pageskin_right: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_pageskin_right,
  gam_unit_interstitial: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_interstitial,
  gam_unit_pixel: ADMIN_GAM_UNIT_DEFAULTS.gam_unit_pixel,
  ads_slot_article_top_enabled: '1',
  ads_slot_article_mid_enabled: '1',
  ads_slot_article_bottom_enabled: '1',
  ads_slot_sidebar_enabled: '1',
  ads_slot_home_top_enabled: '1',
  ads_slot_home_middle_enabled: '1',
  ads_slot_home_bottom_enabled: '1',
  ads_slot_home_mid_enabled: '1',
  ads_slot_category_top_enabled: '1',
  ads_slot_sidebar_tall_enabled: '1',
  ads_slot_skyscraper_left_enabled: '1',
  ads_slot_skyscraper_right_enabled: '1',
  ads_slot_pageskin_left_enabled: '1',
  ads_slot_pageskin_right_enabled: '1',
  ads_slot_interstitial_enabled: '1',
  ads_slot_pixel_enabled: '1',
  ads_global_refresh_sec: '20',
  youtube_shorts_enabled: '1',
  article_video_autoplay: '1',
  article_video_initial_volume: '50',
  google_preferred_source_enabled: '1',
  user_submissions_enabled: '1',
  comments_enabled: '1',
  guest_comments_enabled: '1',
  ...Object.fromEntries(
    Object.values(AD_SLOT_DEFINITIONS).map((def) => [def.refreshSecKey, String(def.defaultRefreshSec)]),
  ),
} as PublicSiteConfig;

let cache: PublicSiteConfig | null = null;
let cacheExpiresAt = 0;
let inflight: Promise<PublicSiteConfig> | null = null;
const CLIENT_CACHE_TTL_MS = 60_000;

function fromEnvFallback(): PublicSiteConfig {
  const legacyHome = process.env.NEXT_PUBLIC_ADSENSE_SLOT_HOME_MID || '';
  const gamNetwork =
    process.env.NEXT_PUBLIC_GAM_AD_UNIT_PATH || process.env.NEXT_PUBLIC_GAM_NETWORK_PATH || GAM_NETWORK_PATH_DEFAULT;
  return {
    ...EMPTY,
    ga_measurement_id: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '',
    facebook_app_id:
      process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '1291989031661965',
    adsense_client: process.env.NEXT_PUBLIC_ADSENSE_CLIENT || '',
    adsense_slot_article_top: process.env.NEXT_PUBLIC_ADSENSE_SLOT_ARTICLE_TOP || '',
    adsense_slot_article_mid: process.env.NEXT_PUBLIC_ADSENSE_SLOT_ARTICLE_MID || '',
    adsense_slot_article_bottom: process.env.NEXT_PUBLIC_ADSENSE_SLOT_ARTICLE_BOTTOM || '',
    adsense_slot_sidebar: process.env.NEXT_PUBLIC_ADSENSE_SLOT_SIDEBAR || '',
    adsense_slot_home_top: process.env.NEXT_PUBLIC_ADSENSE_SLOT_HOME_TOP || legacyHome,
    adsense_slot_home_middle: process.env.NEXT_PUBLIC_ADSENSE_SLOT_HOME_MIDDLE || legacyHome,
    adsense_slot_home_bottom: process.env.NEXT_PUBLIC_ADSENSE_SLOT_HOME_BOTTOM || legacyHome,
    adsense_slot_home_mid: legacyHome,
    gam_network_path: gamNetwork,
    gam_ad_unit_path: gamNetwork,
  };
}

function mergeWithEnv(db: Partial<PublicSiteConfig>): PublicSiteConfig {
  const env = fromEnvFallback();
  const pick = (key: keyof PublicSiteConfig, fallback = '') => {
    const raw = db[key];
    if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
      return String(raw).trim();
    }
    const envVal = env[key];
    return envVal !== undefined && String(envVal).trim() !== '' ? String(envVal).trim() : fallback;
  };

  const merged = { ...EMPTY } as PublicSiteConfig;
  for (const key of Object.keys(EMPTY) as Array<keyof PublicSiteConfig>) {
    merged[key] = pick(key, EMPTY[key]);
  }

  if (!merged.gam_network_path) {
    merged.gam_network_path = merged.gam_ad_unit_path || GAM_NETWORK_PATH_DEFAULT;
  }

  return merged;
}

export function isYoutubeShortsBotEnabled(cfg: PublicSiteConfig): boolean {
  return (cfg.youtube_shorts_enabled || '1') === '1';
}

/** Shorts menüsü, ana sayfa rafı ve /shorts tamamen gizli. */
export function isShortsSectionHidden(cfg: PublicSiteConfig): boolean {
  return cfg.youtube_shorts_enabled === '2';
}

export function isShortsNavVisible(cfg: PublicSiteConfig): boolean {
  return !isShortsSectionHidden(cfg);
}

/** Kullanıcıdan haber gönderme (Footer + /haber-gonder) */
export function isUserSubmissionsEnabled(cfg: PublicSiteConfig): boolean {
  return (cfg.user_submissions_enabled || '1') !== '0';
}

export function isSiteCommentsEnabled(cfg: PublicSiteConfig): boolean {
  return (cfg.comments_enabled || '1') !== '0';
}

export function isGuestCommentsEnabled(cfg: PublicSiteConfig): boolean {
  return (cfg.guest_comments_enabled || '1') !== '0';
}

export async function getPublicSiteConfig(): Promise<PublicSiteConfig> {
  if (cache && cacheExpiresAt > Date.now()) return cache;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/settings/public`, {
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`settings ${res.status}`);
      const json = (await res.json()) as Partial<PublicSiteConfig>;
      cache = mergeWithEnv(json || {});
      cacheExpiresAt = Date.now() + CLIENT_CACHE_TTL_MS;
      return cache;
    } catch {
      cache = mergeWithEnv(EMPTY);
      cacheExpiresAt = Date.now() + CLIENT_CACHE_TTL_MS;
      return cache;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function clearPublicSiteConfigCache() {
  cache = null;
  cacheExpiresAt = 0;
  inflight = null;
}

export function getAdsSlotId(cfg: PublicSiteConfig, slot: string): string {
  const def = getAdSlotDefinition(slot);
  if (!def?.adsenseKey) return '';
  const val = cfg[def.adsenseKey as keyof PublicSiteConfig];
  return typeof val === 'string' ? val : '';
}

export function getAdSlotRefreshSec(cfg: PublicSiteConfig, slot: string): number {
  const def = getAdSlotDefinition(slot);
  if (!def) return 0;
  const raw = cfg[def.refreshSecKey as keyof PublicSiteConfig];
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = parseInt(String(raw).trim(), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return def.defaultRefreshSec;
}

export function isAdsSlotEnabled(cfg: PublicSiteConfig, slot: string): boolean {
  if (cfg.ads_enabled === '0') return false;
  const def = getAdSlotDefinition(slot);
  if (!def) return false;
  const val = cfg[def.enabledKey as keyof PublicSiteConfig];
  return (val ?? '1') !== '0';
}

function isDemoAdSenseClient(client: string, slot: string): boolean {
  return (
    /deneme|000000|test/i.test(client) || /^(1111111111|2222222222|3333333333|4444444444|555555555[1-5])$/.test(slot)
  );
}

export function shouldUseAdSense(cfg: PublicSiteConfig, slot: string): boolean {
  if (cfg.ads_enabled === '0' || cfg.ads_demo !== '0') return false;
  const provider = cfg.ads_provider || 'auto';
  if (provider === 'gam') return false;
  const client = cfg.adsense_client || '';
  const slotId = getAdsSlotId(cfg, slot);
  if (!client || !slotId || isDemoAdSenseClient(client, slotId)) return false;
  if (provider === 'adsense') return true;
  return provider === 'auto';
}

export function shouldUseGam(cfg: PublicSiteConfig): boolean {
  if (cfg.ads_enabled === '0' || cfg.ads_demo !== '0') return false;
  const provider = cfg.ads_provider || 'auto';
  if (provider === 'adsense') return false;
  const network = cfg.gam_network_path?.trim() || cfg.gam_ad_unit_path?.trim();
  if (!network) return false;
  if (provider === 'gam') return true;
  return provider === 'auto';
}

export function resolveGamAdUnitPath(cfg: PublicSiteConfig, slot: string): string {
  const def = getAdSlotDefinition(slot);
  if (!def) return '';

  const network = (cfg.gam_network_path || cfg.gam_ad_unit_path || GAM_NETWORK_PATH_DEFAULT).replace(/\/$/, '');
  const unitKey = def.gamUnitKey as keyof PublicSiteConfig;
  const unitName =
    (typeof cfg[unitKey] === 'string' && cfg[unitKey].trim()) ||
    ADMIN_GAM_UNIT_DEFAULTS[def.gamUnitKey as (typeof ADMIN_GAM_UNIT_KEYS)[number]] ||
    def.defaultGamUnit;

  if (unitName.startsWith('/')) return unitName;
  return `${network}/${unitName}`;
}

export const getPublicSiteConfigServer = reactCache(async (): Promise<PublicSiteConfig> => {
  try {
    const res = await fetch(`${API_SERVER_BASE_URL}/api/settings/public`, {
      next: { revalidate: 60 },
    });
    if (!res.ok) return mergeWithEnv(EMPTY);
    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) return mergeWithEnv(EMPTY)
    const json = (await res.json()) as Partial<PublicSiteConfig>;
    return mergeWithEnv(json || {});
  } catch {
    return mergeWithEnv(EMPTY);
  }
});

export { ADMIN_GAM_UNIT_KEYS, ADMIN_GAM_UNIT_LABELS, ADMIN_GAM_UNIT_DEFAULTS } from '@/lib/ad-slots';
export { AD_SLOT_DEFINITIONS } from '@/lib/ad-slots';
